import { useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError, call } from "../api/client";
import { keys, useAuth } from "../api/queries";
import type { UserView } from "../api/types";

function AuthForm(props: { title: string; intro?: string; submit: string; children: ReactNode; onSubmit: () => Promise<void> }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await props.onSubmit();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="auth">
      <form className="card auth-card" onSubmit={submit}>
        <h1>{props.title}</h1>
        {props.intro && <p className="muted">{props.intro}</p>}
        {props.children}
        {error && <p className="error" role="alert">{error}</p>}
        <button type="submit" className="button primary" disabled={busy}>
          {props.submit}
        </button>
      </form>
    </main>
  );
}

export function SetupPage() {
  const client = useQueryClient();
  const [userName, setUserName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  return (
    <AuthForm
      title="Splitterträger einrichten"
      intro="Lege das erste Konto an. Es bekommt Admin-Rechte."
      submit="Einrichten"
      onSubmit={async () => {
        await call<UserView>(api.POST("/api/auth/setup", { body: { userName, displayName, password } }));
        await client.invalidateQueries({ queryKey: keys.auth });
      }}
    >
      <label className="field">
        <span>Benutzername</span>
        <input autoComplete="username" value={userName} onChange={(e) => setUserName(e.target.value)} required />
      </label>
      <label className="field">
        <span>Anzeigename</span>
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </label>
      <label className="field">
        <span>Passwort (mindestens 8 Zeichen)</span>
        <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
      </label>
    </AuthForm>
  );
}

export function LoginPage() {
  const client = useQueryClient();
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  return (
    <AuthForm
      title="Anmelden"
      submit="Anmelden"
      onSubmit={async () => {
        await call<UserView>(api.POST("/api/auth/login", { body: { userName, password } }));
        await client.invalidateQueries();
      }}
    >
      <label className="field">
        <span>Benutzername</span>
        <input autoComplete="username" value={userName} onChange={(e) => setUserName(e.target.value)} required />
      </label>
      <label className="field">
        <span>Passwort</span>
        <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </label>
    </AuthForm>
  );
}

export function AuthGate(props: { children: ReactNode }) {
  const auth = useAuth();
  if (auth.isLoading) return <p className="center muted">Lade …</p>;
  if (auth.error) return <p className="center error">Der Server ist nicht erreichbar.</p>;
  if (auth.data?.setupRequired) return <SetupPage />;
  if (!auth.data?.user) return <LoginPage />;
  return <>{props.children}</>;
}

export function useCurrentUser(): UserView {
  const auth = useAuth();
  return auth.data!.user!;
}
